import React from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import FlagKZ from "../../../assets/icons/FlagKZ";
import FlagRU from "../../../assets/icons/FlagRU";
import FlagEN from "../../../assets/icons/FlagEN";

/**
 * KK / RU / EN switcher for the landing page.
 *
 * Reuses the app's existing flag icons and language codes. Unlike
 * `src/components/LanguageSelector.js` (which calls window.location.reload()),
 * this one only calls `i18n.changeLanguage` — a full reload on a marketing page
 * would throw away the visitor's scroll position. Persistence still happens:
 * i18next-browser-languagedetector caches to `localStorage.i18nextLng`, exactly
 * as it does for the app.
 */

const LANGUAGES = [
  { id: "kk", name: "Қазақша", short: "KK", Flag: FlagKZ },
  { id: "ru", name: "Русский", short: "RU", Flag: FlagRU },
  { id: "en", name: "English", short: "EN", Flag: FlagEN },
];

export default function LanguageSwitcher({ variant = "menu" }) {
  const { t, i18n } = useTranslation();
  const currentId = LANGUAGES.some((l) => l.id === i18n.language)
    ? i18n.language
    : "kk";
  const current = LANGUAGES.find((l) => l.id === currentId);

  /**
   * Inline row of buttons — used inside the mobile menu, where a nested
   * popover would be awkward and easy to miss.
   */
  if (variant === "inline") {
    return (
      <div>
        <p className="mb-2 text-sm font-medium text-muted">
          {t("landing.nav.language")}
        </p>
        <div className="flex gap-2">
          {LANGUAGES.map(({ id, short, name, Flag }) => {
            const active = id === currentId;
            return (
              <button
                key={id}
                type="button"
                lang={id}
                aria-current={active ? "true" : undefined}
                onClick={() => i18n.changeLanguage(id)}
                className={`inline-flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-line text-black-landing hover:bg-gray-landing"
                }`}
              >
                <Flag className="h-4 w-4 shrink-0 rounded-sm" />
                <span className="sr-only">{name}</span>
                <span aria-hidden="true">{short}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Menu as="div" className="relative">
      <MenuButton
        aria-label={t("landing.nav.language")}
        className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-black-landing transition-colors duration-150 ease-out hover:bg-gray-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <current.Flag className="h-4 w-4 shrink-0 rounded-sm" />
        <span aria-hidden="true">{current.short}</span>
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </MenuButton>

      <MenuItems
        transition
        anchor="bottom end"
        className="z-50 mt-2 w-44 rounded-lg border border-line bg-white p-1 shadow-sm transition duration-150 ease-out focus:outline-none data-[closed]:opacity-0"
      >
        {LANGUAGES.map(({ id, name, Flag }) => (
          <MenuItem key={id}>
            <button
              type="button"
              lang={id}
              onClick={() => i18n.changeLanguage(id)}
              className="flex min-h-11 w-full cursor-pointer items-center gap-2.5 rounded-md px-3 text-left text-sm text-black-landing data-[focus]:bg-gray-landing"
            >
              <Flag className="h-4 w-4 shrink-0 rounded-sm" />
              <span className="flex-1">{name}</span>
              {id === currentId && (
                <Check className="h-4 w-4 text-primary" aria-hidden="true" />
              )}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
